import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Tabs,
  Tab,
  Paper,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatCurrency, formatNumber, getDateRangePresets } from '../../../utils/reportHelpers';
import { API_CONFIG } from '../../../config/api';

interface SiteSummary {
  total_sites: number;
  active_sites: number;
  total_sessions: number;
  charged_sessions: number;
  total_revenue: number;
  avg_utilization: number;
  best_site: string;
}

interface SiteMetric {
  site_id: string;
  site_name: string;
  location: string;
  address: string;
  is_active: boolean;
  total_sessions: number;
  charged_sessions: number;
  total_revenue: number;
  avg_session_duration_minutes: number;
  total_chargers: number;
  available_chargers: number;
  availability_rate: number;
  utilization_rate: number;
}

interface SiteReportData {
  summary: SiteSummary;
  sites: SiteMetric[];
  top_sites: SiteMetric[];
}

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const SiteComparisonReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);
  const [vendorId, setVendorId] = useState('');

  // Filter options state (only vendors - shows all sites for comparison)
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof SiteMetric>('total_sessions');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const tokenData = localStorage.getItem('ev_charging_auth_tokens_v2');
    const token = tokenData ? JSON.parse(tokenData).accessToken : null;

    if (!token) {
      console.error('No auth token found');
      return null;
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  // Fetch vendors on mount (vendors list is independent)
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers) return;

        const response = await fetch(`${API_CONFIG.baseURL}/api/backend/reports/filters/vendors`, {
          headers,
        });
        const data = await response.json();
        setVendors(data.data || []);
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    };

    fetchVendors();
  }, []);

  // Fetch data
  const { data, loading, error, refetch } = useReportData<SiteReportData>({
    endpoint: '/api/backend/reports/site-performance',
    filters: {
      start_date: startDate,
      end_date: endDate,
      ...(vendorId && { vendor_id: vendorId }),
    },
  });

  // Export functionality
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!data?.sites) return;

    exportToCSV(
      data.sites,
      'site-performance',
      [
        { key: 'site_name', label: 'Site' },
        { key: 'location', label: 'Location' },
        ...(canSeeTotalSessions ? [{ key: 'total_sessions', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_sessions', label: 'Charged Sessions', format: 'number' },
        { key: 'total_revenue', label: 'Revenue', format: 'currency' },
        { key: 'avg_session_duration_minutes', label: 'Avg Duration (min)', format: 'number' },
        { key: 'utilization_rate', label: 'Utilization %', format: 'percentage' },
        { key: 'availability_rate', label: 'Availability %', format: 'percentage' },
        { key: 'total_chargers', label: 'Total Chargers', format: 'number' },
        { key: 'available_chargers', label: 'Available Chargers', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    if (vendorId) {
      const selectedVendor = vendors.find(v => v.id === vendorId);
      if (selectedVendor) {
        filters.vendor = { id: vendorId, name: selectedVendor.name };
      }
    }

    await exportToExcel(
      data.sites,
      'site-performance',
      {
        columns: [
          { key: 'site_name', label: translate('reports.table.site', { fallback: 'Site' }) },
          { key: 'location', label: translate('reports.table.location', { fallback: 'Location' }) },
          ...(canSeeTotalSessions ? [{ key: 'total_sessions', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_sessions', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'total_revenue', label: translate('reports.table.revenue', { fallback: 'Revenue' }), format: 'currency' },
          { key: 'avg_session_duration_minutes', label: translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' }), format: 'number' },
          { key: 'utilization_rate', label: translate('reports.table.utilization', { fallback: 'Utilization %' }), format: 'percentage' },
          { key: 'availability_rate', label: translate('reports.table.availability', { fallback: 'Availability %' }), format: 'percentage' },
          { key: 'total_chargers', label: translate('reports.table.chargers', { fallback: 'Total Chargers' }), format: 'number' },
          { key: 'available_chargers', label: translate('reports.table.availableChargers', { fallback: 'Available Chargers' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.siteComparison.title', { fallback: 'Site Comparison' }),
          data: [
            {
              label: translate('reports.kpi.totalSites', { fallback: 'Total Sites' }),
              value: formatNumber(data.summary.total_sites, 0)
            },
            {
              label: translate('reports.kpi.activeSites', { fallback: 'Active Sites' }),
              value: formatNumber(data.summary.active_sites, 0)
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
            {
              label: translate('reports.kpi.totalRevenue', { fallback: 'Total Revenue' }),
              value: formatCurrency(data.summary.total_revenue)
            },
            {
              label: translate('reports.kpi.avgUtilization', { fallback: 'Avg Utilization' }),
              value: formatNumber(data.summary.avg_utilization, 1) + '%'
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!data?.sites) return [];

    return [...data.sites].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.sites, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!data?.sites || data.sites.length === 0) {
      return {
        total_sessions: 0,
        charged_sessions: 0,
        total_revenue: 0,
        total_chargers: 0,
        available_chargers: 0,
        avg_utilization: 0,
        avg_availability: 0,
      };
    }

    const totalSessions = data.sites.reduce((sum, s) => sum + s.total_sessions, 0);
    const chargedSessions = data.sites.reduce((sum, s) => sum + s.charged_sessions, 0);
    const totalRevenue = data.sites.reduce((sum, s) => sum + s.total_revenue, 0);
    const totalChargers = data.sites.reduce((sum, s) => sum + s.total_chargers, 0);
    const availableChargers = data.sites.reduce((sum, s) => sum + s.available_chargers, 0);
    const avgUtilization = data.sites.reduce((sum, s) => sum + s.utilization_rate, 0) / data.sites.length;
    const avgAvailability = data.sites.reduce((sum, s) => sum + s.availability_rate, 0) / data.sites.length;

    return {
      total_sessions: totalSessions,
      charged_sessions: chargedSessions,
      total_revenue: totalRevenue,
      total_chargers: totalChargers,
      available_chargers: availableChargers,
      avg_utilization: avgUtilization,
      avg_availability: avgAvailability,
    };
  }, [data?.sites]);

  const handleSort = (property: keyof SiteMetric) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const topSessionSites = useMemo(() => {
    const source = (data?.top_sites && data.top_sites.length > 0) ? data.top_sites : (data?.sites || []);
    const sorted = [...source].sort((a, b) => b.total_sessions - a.total_sessions);
    return isMobile ? sorted.slice(0, 5) : sorted.slice(0, 10);
  }, [data?.top_sites, data?.sites, isMobile]);

  const topRevenueSites = useMemo(() => {
    const source = (data?.top_sites && data.top_sites.length > 0) ? data.top_sites : (data?.sites || []);
    const sorted = [...source].sort((a, b) => b.total_revenue - a.total_revenue);
    return isMobile ? sorted.slice(0, 5) : sorted.slice(0, 10);
  }, [data?.top_sites, data?.sites, isMobile]);

  const formatSiteLocation = (location: string) =>
    location
      .split(',')
      .map((loc) => loc.trim())
      .map((loc) => translate(`reports.locations.${loc}`, { _: loc }))
      .join(', ');

  return (
    <ReportLayout
      title={translate('reports.siteComparison.title', { fallback: 'Site Comparison' })}
      description={translate('reports.siteComparison.subtitle', { fallback: 'Site-level performance analysis' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          vendorId={vendorId}
          onVendorIdChange={setVendorId}
          vendors={vendors}
          showVendorFilter={true}
          showSiteFilter={false}
          showChargerFilter={false}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1.5, sm: 2 }, pt: 1.5, minWidth: 0 }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minWidth: 0,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>
            </Box>

            {/* Data Table Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.siteDetails', { fallback: 'Detailed Site Metrics' })} ({sortedData.length} sites)
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Paper variant="outlined" sx={mobileRowCardSx}>
                      <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                        {translate('reports.table.total', { fallback: 'Total' })}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        {canSeeTotalSessions && (
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(totals.total_sessions, 0)}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charged_sessions, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatCurrency(totals.total_revenue)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.utilization', { fallback: 'Utilization %' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.avg_utilization, 1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.availability', { fallback: 'Availability %' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.avg_availability, 1)}%</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.chargers', { fallback: 'Chargers' })}</Typography>
                          <Typography sx={mobileValueSx}>{totals.available_chargers}/{totals.total_chargers}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {sortedData.map((site) => (
                      <Paper key={site.site_id} variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.25 }}>
                          {translate('reports.table.site', { fallback: 'Site' })}
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 0.5 }}>
                          {site.site_name}
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, color: 'text.secondary', fontSize: '0.75rem', mb: 1 }}>
                          {site.address}
                        </Typography>

                        <Typography sx={{ ...mobileLabelSx, mb: 0.25 }}>
                          {translate('reports.table.location', { fallback: 'Location' })}
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, mb: 1 }}>
                          {formatSiteLocation(site.location || '-')}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(site.total_sessions, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(site.charged_sessions, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(site.total_revenue)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(site.avg_session_duration_minutes, 1)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.utilization', { fallback: 'Utilization %' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(site.utilization_rate, 1)}%</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.availability', { fallback: 'Availability %' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(site.availability_rate, 1)}%</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.chargers', { fallback: 'Chargers' })}</Typography>
                            <Typography sx={mobileValueSx}>{site.available_chargers}/{site.total_chargers}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'site_name'}
                                direction={orderBy === 'site_name' ? order : 'asc'}
                                onClick={() => handleSort('site_name')}
                              >
                                {translate('reports.table.site', { fallback: 'Site' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'location'}
                                direction={orderBy === 'location' ? order : 'asc'}
                                onClick={() => handleSort('location')}
                              >
                                {translate('reports.table.location', { fallback: 'Location' })}
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'total_sessions'}
                                  direction={orderBy === 'total_sessions' ? order : 'asc'}
                                  onClick={() => handleSort('total_sessions')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charged_sessions'}
                                  direction={orderBy === 'charged_sessions' ? order : 'asc'}
                                  onClick={() => handleSort('charged_sessions')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'total_revenue'}
                                direction={orderBy === 'total_revenue' ? order : 'asc'}
                                onClick={() => handleSort('total_revenue')}
                              >
                                {translate('reports.table.revenue', { fallback: 'Revenue' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'avg_session_duration_minutes'}
                                direction={orderBy === 'avg_session_duration_minutes' ? order : 'asc'}
                                onClick={() => handleSort('avg_session_duration_minutes')}
                              >
                                {translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'utilization_rate'}
                                direction={orderBy === 'utilization_rate' ? order : 'asc'}
                                onClick={() => handleSort('utilization_rate')}
                              >
                                {translate('reports.table.utilization', { fallback: 'Utilization %' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'availability_rate'}
                                direction={orderBy === 'availability_rate' ? order : 'asc'}
                                onClick={() => handleSort('availability_rate')}
                              >
                                {translate('reports.table.availability', { fallback: 'Availability %' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'total_chargers'}
                                direction={orderBy === 'total_chargers' ? order : 'asc'}
                                onClick={() => handleSort('total_chargers')}
                              >
                                {translate('reports.table.chargers', { fallback: 'Chargers' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((site) => (
                            <TableRow key={site.site_id} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {site.site_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#909090' }}>
                                    {site.address}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatSiteLocation(site.location || '-')}</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(site.total_sessions, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(site.charged_sessions, 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(site.total_revenue)}</TableCell>
                              <TableCell align="right">{formatNumber(site.avg_session_duration_minutes, 1)}</TableCell>
                              <TableCell align="right">{formatNumber(site.utilization_rate, 1)}%</TableCell>
                              <TableCell align="right">{formatNumber(site.availability_rate, 1)}%</TableCell>
                              <TableCell align="right">{site.available_chargers}/{site.total_chargers}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow
                            sx={{
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                fontWeight: 600,
                                zIndex: 2,
                              }
                            }}
                          >
                            <TableCell colSpan={2}>{translate('reports.table.total', { fallback: 'Total' })}</TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">{formatNumber(totals.total_sessions, 0)}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(totals.charged_sessions, 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.total_revenue)}</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">{formatNumber(totals.avg_utilization, 1)}%</TableCell>
                            <TableCell align="right">{formatNumber(totals.avg_availability, 1)}%</TableCell>
                            <TableCell align="right">{totals.available_chargers}/{totals.total_chargers}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, mt: 3, minWidth: 0 }}>
                  {/* Top Sites by Sessions */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.topSitesSessions', { fallback: 'Top Sites by Sessions' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                      {isMobile ? (
                        <BarChart data={topSessionSites} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            tickFormatter={(value) => formatNumber(value, 0)}
                          />
                          <YAxis
                            type="category"
                            dataKey="site_name"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            width={90}
                            tickFormatter={(value) => String(value).slice(0, 14)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatNumber(value, 0)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey={canSeeTotalSessions ? 'total_sessions' : 'charged_sessions'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={topSessionSites}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            dataKey="site_name"
                            tick={{ fontSize: 11 }}
                            stroke="#606060"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            tickFormatter={(value) => formatNumber(value, 0)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatNumber(value, 0)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey={canSeeTotalSessions ? 'total_sessions' : 'charged_sessions'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>

                  {/* Top Sites by Revenue */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.topSitesRevenue', { fallback: 'Top Sites by Revenue' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                      {isMobile ? (
                        <BarChart data={topRevenueSites} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                          />
                          <YAxis
                            type="category"
                            dataKey="site_name"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            width={90}
                            tickFormatter={(value) => String(value).slice(0, 14)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="total_revenue" fill="#2196F3" name="Revenue" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={topRevenueSites}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            dataKey="site_name"
                            tick={{ fontSize: 11 }}
                            stroke="#606060"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                          />
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="total_revenue" fill="#2196F3" name="Revenue" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
